import { useEffect, useState } from 'react';
import { Dialog, DialogTrigger, Modal, ModalOverlay } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import Button from 'src/components/Button/Button';
import InputText from 'src/components/InputText/InputText';
import RadioGroup from 'src/components/RadioGroup/RadioGroup';
import * as yup from 'yup';

import { getUser } from '@/api/getUser';
import { updateInvToken } from '@/api/updateInvToken';
import MultiSelectField from '@/components/SelectField/MultiSelectField';
import { UserAtom } from '@/jotai/jotai';
import { Office } from '@/types/office';
import { User } from '@/types/user';
import { UserRequest } from '@/types/user';

type UserFormProps = {
  user: User | undefined;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubmit: SubmitHandler<UserRequest>;
  onDelete: (user: User) => void;
  offices: Office[];
};

const EditUserForm = ({ isOpen, setIsOpen, user, onSubmit, onDelete, offices }: UserFormProps) => {
  const [data, setData] = useState<User>();

  const [loginUser] = useAtom(UserAtom);

  useEffect(() => {
    if (!user) return;
    const getUsers = async () => {
      const result = await getUser(user.email, user.role);
      if (result.status === 200) {
        const u: User = {
          last_name: result.result.last_name,
          first_name: result.result.first_name,
          last_name_kana: result.result.last_name_kana,
          first_name_kana: result.result.first_name_kana,
          email: result.result.email,
          phone_number: result.result.phone_number,
          line_invite_link: result.result.line_invite_link,
          role: result.result.role,
          office_ids: result.result.office_ids,
          children: result.result.children
            ? result.result.children.slice(0, 2).map((child) => ({
                last_name: child.last_name,
                first_name: child.first_name,
                office_ids: child.office_ids,
              }))
            : undefined,
          is_super_admin: result.result.is_super_admin,
        };
        setData(u);
      }
    };
    getUsers();
  }, [user]);

  const schema: yup.ObjectSchema<UserRequest> = yup
    .object({
      last_name: yup.string().required('アカウント名（姓）は必須です'),
      first_name: yup.string().optional(),
      last_name_kana: yup.string().required('アカウント名（セイ）は必須です'),
      first_name_kana: yup.string().optional(),
      phone_number: yup.string().optional(),
      line_invite_link: yup.string().optional(),
      email: yup
        .string()
        .email('メールアドレスの形式が正しくありません')
        .required('メールアドレスは必須です'),
      role: yup
        .string()
        .oneOf(['admin', 'staff', 'p_admin', 'p_user'])
        .required('アカウント権限は必須です'),
      office_ids: yup.array().of(yup.number().defined()).required('事業所は必須です'),
      children: yup.array().optional(),
    })
    .required();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<UserRequest>({
    values: data,
    resolver: yupResolver(schema),
  });

  const handleCopy = async (copyText: string) => {
    await navigator.clipboard.writeText(copyText);
  };

  const handleRegen = async (user: User) => {
    const result = await updateInvToken(user);
    if (result.status === 200 && data) {
      const updatedData: User = { ...data, line_invite_link: result.result.token };
      setData(updatedData);
      setValue('line_invite_link', result.result.token);
      reset(updatedData);
    }
  };

  if (!data) return <></>;

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <ModalOverlay>
        <Modal
          key={`edit-user-modal`}
          isDismissable
          className="fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-black/50"
        >
          <Dialog
            role="dialog"
            aria-label="アカウント編集"
            className="mt-24 h-full overflow-y-auto"
          >
            <div className="m-auto rounded-3xl bg-white p-10">
              <div className="flex flex-row justify-between">
                <h1 className="mb-5 text-2xl font-700">アカウント編集</h1>
                <DialogTrigger>
                  <Button
                    className="text-gray-400"
                    variant="outline"
                    size="lg"
                    onClick={() => setIsOpen(true)}
                  >
                    削除
                  </Button>
                  <ModalOverlay>
                    <Modal
                      isDismissable
                      isOpen={isOpen}
                      className="fixed right-0 top-0 z-50 flex h-full w-full items-center justify-center bg-black/50"
                    >
                      <Dialog
                        key={`delete-user-modal`}
                        role="dialog"
                      >
                        {({ close: close2 }) => (
                          <div className="m-auto flex min-h-[300px] max-w-[500px] flex-col items-center justify-center rounded-3xl bg-white outline outline-1 md:w-full">
                            <div className="space-y-8 p-8">
                              <h2 className="mb-6 text-2xl font-bold">アカウントを削除</h2>
                              <p className="text-center text-lg">
                                アカウントを削除しますか。この操作は元に戻せません。
                                ※登録済みの利用者データがある場合は削除できません。
                              </p>
                              <div className="flex flex-col justify-between space-y-2">
                                <Button
                                  size="lg"
                                  variant="solid-fill"
                                  onClick={() => onDelete(data)}
                                >
                                  削除
                                </Button>
                                <Button
                                  size="lg"
                                  variant="text"
                                  slot="close"
                                  onClick={close2}
                                >
                                  キャンセル
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </Dialog>
                    </Modal>
                  </ModalOverlay>
                </DialogTrigger>
              </div>
              <form
                key={`edit-user-form-${data.email}`}
                onSubmit={handleSubmit(onSubmit)}
                className="w-full p-6"
              >
                <div className="flex justify-between space-x-2 p-4">
                  <div className="flex w-full flex-col justify-between space-y-2">
                    <div>メールアドレス</div>
                    <span>{data?.email}</span>
                  </div>

                  <div className="flex w-full flex-col justify-between space-y-2">
                    <div>アカウント権限</div>
                    <span>{data?.role}</span>
                  </div>
                </div>

                {data?.role !== 'admin' && (
                  <div className="flex flex-col justify-between space-y-2 p-4">
                    <div className="flex flex-row items-center gap-4">
                      <div>LINE招待URL</div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopy(data?.line_invite_link || '')}
                      >
                        Copy
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="solid-fill"
                        onClick={() => handleRegen(data)}
                      >
                        再生成
                      </Button>
                    </div>
                    <span className="text-sm">{data?.line_invite_link}</span>
                  </div>
                )}

                <div className="flex justify-between space-x-2 p-4">
                  <InputText
                    type="text"
                    required
                    placeholder="山田"
                    label="アカウント名（姓）"
                    errors={errors.last_name}
                    {...register('last_name', { required: true })}
                  />
                  <InputText
                    type="text"
                    label="アカウント名（名）"
                    placeholder="太郎"
                    errors={errors.first_name}
                    {...register('first_name')}
                  />
                </div>

                <div className="flex justify-between space-x-2 p-4">
                  <InputText
                    type="text"
                    required
                    placeholder="ヤマダ"
                    label="アカウント名（セイ）"
                    errors={errors.last_name_kana}
                    {...register('last_name_kana', { required: true })}
                  />
                  <InputText
                    type="text"
                    label="アカウント名（メイ）"
                    placeholder="タロウ"
                    errors={errors.first_name_kana}
                    {...register('first_name_kana')}
                  />
                </div>

                <div className="flex justify-between space-x-2 p-4">
                  <InputText
                    type="tel"
                    label="連絡先TEL"
                    placeholder="08012345678"
                    errors={errors.phone_number}
                    {...register('phone_number')}
                  />
                  <InputText
                    disabled
                    type="email"
                    required
                    placeholder="example@pla.co.jp"
                    label="メールアドレス"
                    errors={errors.email}
                    {...register('email', { required: true })}
                  />
                </div>

                <RadioGroup
                  title="アカウント権限"
                  required
                  disabled
                  supportText="権限を選択してください"
                  errors={errors.role}
                  options={[
                    {
                      id: 'admin',
                      label: '管理者（admin）',
                      value: 'admin',
                    },
                    {
                      id: 'staff',
                      label: '一般（staff）',
                      value: 'staff',
                    },
                    {
                      id: 'p_admin',
                      label: '保護者（p_admin）',
                      value: 'p_admin',
                    },
                    {
                      id: 'p_user',
                      label: '保護者（p_user）',
                      value: 'p_user',
                    },
                  ]}
                  {...register('role')}
                />

                <MultiSelectField
                  name="office_ids"
                  label="事業所を選択してください"
                  options={offices
                    .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
                    .map((office) => ({
                      label: office.name,
                      value: office.id,
                    }))}
                  defaultValue={offices
                    .filter((office) => data.office_ids.includes(office.id))
                    .map((office) => ({
                      label: office.name,
                      value: office.id,
                    }))}
                  control={control}
                  required
                />

                {watch('role') === 'p_admin' && (
                  <>
                    <div className="flex justify-between space-x-2 p-4">
                      <InputText
                        type="text"
                        label="児童名（姓）"
                        placeholder="山田"
                        required={
                          watch('children.0.last_name') !== '' ||
                          watch('children.0.first_name') !== ''
                            ? true
                            : false
                        }
                        errors={errors.children?.[0]?.last_name}
                        {...register('children.0.last_name')}
                      />
                      <InputText
                        type="text"
                        label="児童名（名）"
                        placeholder="孝之"
                        required={
                          watch('children.0.last_name') !== '' ||
                          watch('children.0.first_name') !== ''
                            ? true
                            : false
                        }
                        errors={errors.children?.[0]?.first_name}
                        {...register('children.0.first_name')}
                      />
                      <MultiSelectField
                        name="children.0.office_ids"
                        label="事業所を選択"
                        options={offices
                          .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
                          .map((office) => ({
                            label: office.name,
                            value: office.id,
                          }))}
                        defaultValue={offices
                          .filter(
                            (office) => data.children?.[0]?.office_ids?.includes(office.id) ?? false
                          )
                          .map((office) => ({
                            label: office.name,
                            value: office.id,
                          }))}
                        control={control}
                        required
                      />
                    </div>
                    <div className="flex justify-between space-x-2 p-4">
                      <InputText
                        type="text"
                        label="児童名（姓）"
                        placeholder="山田"
                        required={
                          watch('children.1.last_name') !== '' ||
                          watch('children.1.first_name') !== ''
                            ? true
                            : false
                        }
                        errors={errors.children?.[1]?.last_name}
                        {...register('children.1.last_name')}
                      />
                      <InputText
                        type="text"
                        label="児童名（名）"
                        placeholder="孝之"
                        required={
                          watch('children.1.last_name') !== '' ||
                          watch('children.1.first_name') !== ''
                            ? true
                            : false
                        }
                        errors={errors.children?.[1]?.first_name}
                        {...register('children.1.first_name')}
                      />
                      <MultiSelectField
                        name="children.1.office_ids"
                        label="事業所選択"
                        options={offices
                          .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
                          .map((office) => ({
                            label: office.name,
                            value: office.id,
                          }))}
                        defaultValue={offices
                          .filter(
                            (office) => data.children?.[1]?.office_ids?.includes(office.id) ?? false
                          )
                          .map((office) => ({
                            label: office.name,
                            value: office.id,
                          }))}
                        control={control}
                      />
                    </div>
                  </>
                )}

                <div className="mt-10 flex items-center justify-center space-x-2">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setIsOpen(false)}
                  >
                    戻る
                  </Button>
                  <Button
                    variant="solid-fill"
                    size="lg"
                    disabled={Object.keys(errors).length > 0}
                    type="submit"
                  >
                    保存
                  </Button>
                </div>
              </form>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};

export default EditUserForm;
